import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { getStreamStatus } from "../components/services/liveService";
import { getStoredToken, getUser } from "../utils/auth";
import useIvsPlayer from "./useIvsPlayer";

const SOCKET_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "").replace(/\/api$/i, "");
// live-status-updated carries the public status (SCHEDULED / LIVE / PAUSED / ENDED / SUSPENDED)
const PLAYABLE_STATUSES = ["live", "paused"];
// Each stream type has its own viewer page
const VIEWER_PATHS = { marketplace: "/live-shopping", donation: "/stream", live: "/stream" };
// Viewers watching when the live ends are told so, then taken home
const END_REDIRECT_SECONDS = 5;
const END_REDIRECT_PATH = "/home";

/**
 * Viewer side of a live: stream details, low-latency playback into `videoRef`, and the stream room socket
 * (viewer count, live status, comments on/off). Streams of another type are redirected to their own page.
 * When the live ends while watching, viewers are sent home after a short countdown (held while `holdRedirect`).
 * `playback: false` stops the player, e.g. while the viewer is on stage and gets the live from the stage instead.
 */
const useLiveViewer = (streamId, streamType, videoRef, { holdRedirect = false, playback = true } = {}) => {
  const navigate = useNavigate();
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [socket, setSocket] = useState(null);
  const [redirectIn, setRedirectIn] = useState(null);

  useEffect(() => {
    setRedirectIn(null);
    getStreamStatus(streamId)
      .then((result) => {
        if (VIEWER_PATHS[result.stream_type] !== VIEWER_PATHS[streamType]) {
          navigate(`${VIEWER_PATHS[result.stream_type]}/${streamId}`, { replace: true });
          return;
        }
        setStream(result);
        setStatus(result.status);
        setChatEnabled(result.chat_enabled);
        setViewerCount(result.viewer_count || 0);
      })
      .catch((err) => setError(err.message || "Unable to load this live"));
  }, [streamId, streamType, navigate]);

  useIvsPlayer(videoRef, stream?.playback_url, playback && PLAYABLE_STATUSES.includes(status));

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // A live that had already ended when the page opened has nothing to listen to
  const streamLoaded = !!stream && stream.status !== "ended";
  useEffect(() => {
    if (!streamLoaded) return;
    const userId = getUser()?.id;
    const s = io(SOCKET_URL, { transports: ["websocket"], auth: { token: getStoredToken() } });

    // Rooms belong to the connection, so they are joined again after a reconnect.
    // The engagement room also joins the personal room, which is how a block reaches this viewer.
    s.on("connect", () => {
      s.emit("join-stream", { streamId, userId });
      s.emit("join-stream-engagement", { streamId, userId });
    });
    s.on("viewer-count-updated", (data) => setViewerCount(data.viewerCount || 0));
    // Only a live that ends while watching sends the viewer home; joining reports ENDED for a finished live
    const onEnded = () => {
      if (statusRef.current === "ended") return;
      setStatus("ended");
      setRedirectIn((current) => current ?? END_REDIRECT_SECONDS);
    };
    s.on("live-status-updated", (data) => {
      const nextStatus = String(data.status || "").toLowerCase();
      if (nextStatus === "ended") onEnded();
      else setStatus(nextStatus);
    });
    s.on("stream-ended", onEnded);
    s.on("comments-toggled", (data) => setChatEnabled(!!data.chat_enabled));
    // Settings the host changes during the live (gifting, reactions, audience, layout)
    s.on("live-settings-updated", (data) => setStream((prev) => prev && { ...prev, ...data }));
    s.on("donation-status-changed", (data) =>
      setStream((prev) => prev && { ...prev, donation_enabled: !!data.donation_enabled })
    );
    s.on("join-stream-error", (data) => setError(data.message));
    s.on("removed-from-stream", (data) => {
      setError(data.message);
      setStatus("removed");
    });
    setSocket(s);

    return () => {
      s.emit("leave-stream", { streamId, userId });
      s.emit("leave-stream-engagement", { streamId, userId });
      s.disconnect();
      setSocket(null);
    };
  }, [streamLoaded, streamId]);

  useEffect(() => {
    if (redirectIn === null || holdRedirect) return;
    if (redirectIn <= 0) {
      navigate(END_REDIRECT_PATH, { replace: true });
      return;
    }
    const timer = setTimeout(() => setRedirectIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [redirectIn, holdRedirect, navigate]);

  const statusMessage =
    (redirectIn !== null &&
      `This live has ended. Taking you to the home page in ${Math.max(redirectIn, 0)}s...`) ||
    error ||
    (status === "ended" && "This live has ended") ||
    (status === "paused" && "The host has paused the live") ||
    (status && !PLAYABLE_STATUSES.includes(status) && "This live hasn't started yet");

  return { stream, status, statusMessage, viewerCount, chatEnabled, socket };
};

export default useLiveViewer;
